import api from '../api';
import { tokenStorage } from '../utils/tokenStorage';
import toastService from './toastService';
import {
  ChatSession,
  ChatMessage,
  CreateSessionRequest,
  SendMessageRequest,
  SendMessageResponse,
  DocumentSearchRequest,
  SearchResult,
  RAGStats,
  DocumentRAGStatus,
  ChatAnalytics,
  StreamChunk
} from '../types/chat';

class ChatAPI {
  // Session Management
  async getSessions(limit: number = 50, offset: number = 0, project_id?: number): Promise<ChatSession[]> {
    const params: any = { limit, offset, include_count: true };
    if (project_id) {
      params.project_id = project_id;
    }

    const response = await api.get('/api/chat/sessions', {
      params
    });
    return response.data.data;
  }

  async getSession(sessionId: string): Promise<ChatSession> {
    const response = await api.get(`/api/chat/sessions/${sessionId}`);
    return response.data.data;
  }

  async createSession(request: CreateSessionRequest): Promise<ChatSession> {
    try {
      const response = await api.post('/api/chat/sessions', request);
      toastService.success('Chat session created successfully');
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Failed to create chat session';
      toastService.error(errorMsg);
      throw error;
    }
  }

  async updateSession(sessionId: string, updates: Partial<ChatSession>): Promise<ChatSession> {
    const response = await api.put(`/api/chat/sessions/${sessionId}`, updates);
    return response.data.data;
  }

  async deleteSession(sessionId: string): Promise<void> {
    try {
      await api.delete(`/api/chat/sessions/${sessionId}`);
      toastService.success('Chat session deleted successfully');
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Failed to delete chat session';
      toastService.error(errorMsg);
      throw error;
    }
  }

  // Message Management
  async getMessages(
    sessionId: string,
    limit: number = 100,
    offset: number = 0
  ): Promise<ChatMessage[]> {
    const response = await api.get(`/api/chat/sessions/${sessionId}/messages`, {
      params: { limit, offset, include_references: true }
    });
    return response.data.data;
  }

  async sendMessage(sessionId: string, message: string): Promise<SendMessageResponse> {
    const response = await api.post(`/api/chat/sessions/${sessionId}/messages`, {
      message,
      stream: false
    });
    return response.data.data;
  }

  async sendMessageStream(
    sessionId: string,
    message: string,
    onChunk: (chunk: string) => void,
    onComplete: (message: ChatMessage) => void,
    onError?: (error: string) => void,
    onArtifact?: (artifact: import('../types/chat').ChatArtifact) => void
  ): Promise<void> {
    try {
      const response = await fetch(`${api.defaults.baseURL}/api/chat/sessions/${sessionId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jarvis_access_token')}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          message,
          stream: true
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body reader');
      }

      const decoder = new TextDecoder();

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data: StreamChunk = JSON.parse(line.slice(6));

                switch (data.type) {
                  case 'content':
                    if (data.content) {
                      onChunk(data.content);
                    }
                    break;

                  case 'artifact':
                    if (onArtifact && data.artifact) {
                      onArtifact(data.artifact);
                    }
                    break;

                  case 'complete':
                    if (data.message) {
                      onComplete(data.message);
                    }
                    break;

                  case 'error':
                    if (onError && data.error) {
                      onError(data.error);
                    }
                    break;
                }
              } catch (parseError) {
                console.warn('Failed to parse SSE data:', parseError);
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    } catch (error) {
      console.error('Stream error:', error);
      if (onError) {
        onError(error instanceof Error ? error.message : 'Unknown error');
      }
      throw error;
    }
  }

  // Download a generated file artifact
  downloadFile(fileId: string, filename: string): void {
    const token = localStorage.getItem('jarvis_access_token');
    const url = `${api.defaults.baseURL}/api/chat/files/${fileId}`;
    fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include'  // send cookies as fallback auth
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`Download failed: HTTP ${res.status}`);
        }
        return res.blob();
      })
      .then(blob => {
        const objectUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objectUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
      })
      .catch(err => {
        console.error('[chatAPI] downloadFile error:', err.message);
      });
  }

  // Document Management
  async addDocumentsToSession(sessionId: string, documentIds: number[]): Promise<ChatSession> {
    const response = await api.post(`/api/chat/sessions/${sessionId}/documents`, {
      documentIds
    });
    return response.data.data;
  }

  async removeDocumentsFromSession(sessionId: string, documentIds: number[]): Promise<ChatSession> {
    const response = await api.delete(`/api/chat/sessions/${sessionId}/documents`, {
      data: { documentIds }
    });
    return response.data.data;
  }

  async uploadDocument(sessionId: string, file: File): Promise<{ document: any; session: ChatSession }> {
    try {
      const formData = new FormData();
      formData.append('document', file);

      const response = await api.post(`/api/chat/sessions/${sessionId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000,
      });
      if (response.data.skipped) {
        // Same file already present in this project — do not claim a fresh upload succeeded.
        toastService.info(response.data.message || `"${file.name}" is already uploaded — skipped.`);
      } else {
        toastService.success(`Document "${file.name}" uploaded successfully`);
      }
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Failed to upload document';
      toastService.error(errorMsg);
      throw error;
    }
  }

  async getUserDocuments(options: { limit?: number; offset?: number; category?: string; project_id?: number } = {}): Promise<any[]> {
    const response = await api.get('/api/chat/documents', {
      params: options
    });
    return response.data.data;
  }

  async searchDocuments(query: string, options: Omit<DocumentSearchRequest, 'q'> = {}): Promise<SearchResult[]> {
    const response = await api.get('/api/chat/documents/search', {
      params: { q: query, ...options }
    });
    return response.data.data;
  }

  // Analytics and Stats
  async getSessionStats(sessionId: string): Promise<ChatAnalytics> {
    const response = await api.get(`/api/chat/sessions/${sessionId}/stats`);
    return response.data.data;
  }

  async getSessionSummary(sessionId: string): Promise<{ summary: string }> {
    const response = await api.get(`/api/chat/sessions/${sessionId}/summary`);
    return response.data.data;
  }

  async regenerateResponse(sessionId: string, messageId: string): Promise<SendMessageResponse> {
    const response = await api.post(`/api/chat/sessions/${sessionId}/regenerate/${messageId}`);
    return response.data.data;
  }

  // RAG Management
  async getRAGStats(): Promise<RAGStats> {
    const response = await api.get('/api/rag/stats');
    return response.data.data;
  }

  async getDocumentStatus(documentId: number): Promise<DocumentRAGStatus> {
    const response = await api.get(`/api/rag/documents/${documentId}/status`);
    return response.data.data;
  }

  async getDocumentRAGStatus(documentId: number): Promise<any> {
    const response = await api.get(`/api/rag/documents/${documentId}/status`);
    return response.data.data;
  }

  async processDocument(documentId: number): Promise<any> {
    try {
      const response = await api.post(`/api/rag/documents/${documentId}/process`);
      toastService.success('Document processing started');
      return response.data.data;
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Failed to process document';
      toastService.error(errorMsg);
      throw error;
    }
  }

  async reprocessDocument(documentId: number): Promise<any> {
    const response = await api.post(`/api/rag/documents/${documentId}/reprocess`);
    return response.data.data;
  }

  async bulkProcessDocuments(documentIds: number[]): Promise<any> {
    const response = await api.post('/api/rag/documents/bulk-process', {
      documentIds
    });
    return response.data.data;
  }

  async semanticSearch(
    query: string,
    options: {
      documents?: number[];
      limit?: number;
      type?: 'semantic' | 'hybrid';
      threshold?: number;
    } = {}
  ): Promise<SearchResult[]> {
    const response = await api.get('/api/rag/search', {
      params: { q: query, ...options }
    });
    return response.data.data.results;
  }

  async searchWithFilters(
    query: string,
    filters: {
      categories?: string[];
      date_start?: string;
      date_end?: string;
      size_min?: number;
      size_max?: number;
      limit?: number;
    } = {}
  ): Promise<SearchResult[]> {
    const response = await api.get('/api/rag/search/filters', {
      params: { q: query, ...filters }
    });
    return response.data.data.results;
  }

  // AI Model Management
  async getAvailableModels(): Promise<any[]> {
    try {
      const response = await api.get('/api/analysis/ai-models');
      return response.data.models || [];
    } catch (error: any) {
      console.error('Failed to fetch available AI models:', error);
      // Return default Claude model as fallback
      return [{
        id: 'claude-4-sonnet',
        name: 'Claude 4 Sonnet',
        provider: 'Anthropic',
        available: true,
        features: ['text', 'vision', 'analysis'],
        maxTokens: 8192
      }];
    }
  }

  // Utility Methods
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    if (diffDays <= 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    if (diffDays <= 365) return `${Math.ceil(diffDays / 30)} months ago`;

    return date.toLocaleDateString();
  }

  calculateReadingTime(text: string): number {
    const wordsPerMinute = 200;
    const words = text.trim().split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
  }

  // Debug methods
  async getDocumentChunks(documentId: number): Promise<any> {
    const response = await api.get(`/api/chat/documents/${documentId}/chunks`);
    return response.data.data;
  }

  async debugVectorSearch(query: string, documentIds: number[]): Promise<any> {
    const response = await api.post('/api/chat/debug/vector-search', {
      query,
      documentIds
    });
    return response.data.data;
  }

  extractKeywords(text: string, count: number = 5): string[] {
    const stopWords = new Set([
      'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by',
      'is', 'are', 'was', 'were', 'been', 'be', 'have', 'has', 'had', 'do', 'does',
      'did', 'will', 'would', 'could', 'should', 'may', 'might', 'can', 'about',
      'what', 'how', 'when', 'where', 'why', 'who', 'this', 'that', 'these', 'those'
    ]);

    const words = text
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 3 && !stopWords.has(word));

    const frequency: { [key: string]: number } = {};
    words.forEach(word => {
      frequency[word] = (frequency[word] || 0) + 1;
    });

    return Object.entries(frequency)
      .sort(([, a], [, b]) => b - a)
      .slice(0, count)
      .map(([word]) => word);
  }
}

export const chatAPI = new ChatAPI();